import { useColorMode } from '@chakra-ui/react';
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { defaultCalendarSettings } from '../../lib/constants';

import type { ICalendarPlusSettings } from '../../lib/types';

interface CalendarSettingsContextType {
	settings: ICalendarPlusSettings;
}

// Create the context with default values
const CalendarSettingsContext = createContext<CalendarSettingsContextType | undefined>(undefined);

export const CalendarSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { colorMode } = useColorMode();
	const [settings, setSettings] = useState<ICalendarPlusSettings>(defaultCalendarSettings);

	useEffect(() => {
		if (typeof window.calendarPlusSettings !== 'undefined') {
			setSettings((prevSettings) => ({
				...prevSettings,
				...window.calendarPlusSettings,
				styles: {
					light: { ...prevSettings.styles.light, ...window.calendarPlusSettings?.styles?.light },
					dark: { ...prevSettings.styles.dark, ...window.calendarPlusSettings?.styles?.dark },
				},
			}));
		}
	}, []);

	useEffect(() => {
		if (settings.styles[colorMode]?.calendarTodayCellBackgroundColor) {
			document.documentElement.style.setProperty(
				'--today-background-color',
				settings.styles[colorMode].calendarTodayCellBackgroundColor
			);
		}
		if (settings.styles[colorMode]?.calendarBordersColor) {
			document.documentElement.style.setProperty(
				'--calendar-borders-color',
				settings.styles[colorMode].calendarBordersColor
			);
		}
	}, [settings, colorMode]);

	const { timeFormat, monthFormat, dateFormat } = useMemo(() => {
		return {
			timeFormat: settings.timeFormat === 'custom' ? settings.customTimeFormat : settings.timeFormat,
			monthFormat: settings.monthFormat === 'custom' ? settings.customMonthFormat : settings.monthFormat,
			dateFormat: settings.dateFormat === 'custom' ? settings.customDateFormat : settings.dateFormat,
		};
	}, [settings]);

	return (
		<CalendarSettingsContext.Provider
			value={{ settings: { ...settings, formats: { timeFormat, monthFormat, dateFormat } } }}
		>
			{children}
		</CalendarSettingsContext.Provider>
	);
};

export const useCalendarSettings = (): CalendarSettingsContextType => {
	const context = useContext(CalendarSettingsContext);
	if (!context) {
		throw new Error('useCalendarSettings must be used within a CalendarSettingsProvider');
	}
	return context;
};
